import {
  appTemplatesPageLoaded,
  appTemplatesUserClicked,
} from '@wix/bi-logger-developer-center/v2';
import {
  CLI_APP_OPTIONS_MODAL_NAME,
  CLI_APP_OPTIONS_MODAL_ROUTE,
  buildCliModalLoadedEvent,
  buildCliModalCtaClickEvent,
} from '@wix/dev-center-pages-common';
import { getCurrentUrl, getReferrer } from '../services/getUrlsForBi';
import { useBiLogger } from '../context/BiContext';
import type { SelfHostedModalBiParams } from '../components/TemplatePage/Modals/SelfHostedModal/types';
import type {
  DeploymentHostType,
  PackageManager,
} from '@wix/ambassador-devcenter-apps-v1-app-template/types';
import { ROUTES } from '../constants/routes';
import type { PrerequisiteStep } from '../components/TemplatePage/Modals/CLIModal/PreSteps';
import { createStore } from 'reusable';

export const useTemplatesPageBi = createStore(() => {
  const { logger } = useBiLogger();

  const reportTagClicked = (
    templateId: string,
    tagTitle: string,
    tagUrl: string,
  ) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'external_url_docs',
        click_name: `template tag clicked: ${tagTitle}`,
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: tagUrl,
      }),
    );
  };
  const reportCopyToClipboardClicked = (templateId: string, biName: string) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'action',
        click_name: `${biName}: copy code`,
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: '',
      }),
    );
  };

  const reportClickOnTab = (tabName: string) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'filter_view',
        click_name: `select framework ${tabName}`,
        from_url: getCurrentUrl(),
        guid_type: 'irrelevant',
        guid_value: '11111111-1111-1111-1111-111111111111',
        to_url: '',
      }),
    );
  };

  const reportMainPageLoaded = () => {
    void logger.report(
      appTemplatesPageLoaded({
        page_title: 'app templates',
        page_name: 'homepage',
        page_type: 'Page',
        from_url: getReferrer(),
        to_url: getCurrentUrl(),
      }),
    );
  };
  const reportCloseCliModalClick = (templateId: string) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'close modal',
        click_name: 'CLI template close modal',
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: '',
      }),
    );
  };

  const reportAppTestingDocsClicked = (appId: string) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'external_url_docs',
        click_name: 'template modal | go to app testing docs',
        from_url: getCurrentUrl(),
        guid_type: 'app_id',
        guid_value: appId,
        to_url: ROUTES.appChecksAndTestingDocs,
      }),
    );
  };

  const reportImagesCarouselChange = (templateId: string) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'action',
        click_name: `template images carousel change`,
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: '',
      }),
    );
  };

  const reportProviderSelected = (
    templateId: string,
    providerId: DeploymentHostType,
  ) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'action',
        click_name: `select deployment provider: ${providerId}`,
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: '',
      }),
    );
  };

  const reportCloseButtonClick = ({
    templateId,
    appId,
    biParams,
  }: {
    templateId: string;
    appId?: string;
    biParams: SelfHostedModalBiParams;
  }) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'action',
        click_name: `close modal in step ${biParams.stepName}`,
        from_url: getCurrentUrl(),
        guid_type: appId ? 'app_id' : 'template_id',
        guid_value: appId || templateId,
        to_url: '',
      }),
    );
  };

  const reportRelatedTemplateClick = (
    templateId: string,
    externalUrl?: string,
  ) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'navigation_button',
        click_name: 'related template click',
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: externalUrl,
      }),
    );
  };
  const reportMainCTAClick = ({
    templateId,
    appId,
    biParams,
  }: {
    templateId: string;
    appId?: string;
    biParams: SelfHostedModalBiParams;
  }) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'modal_step_forward',
        click_name: `${biParams.stepName}: ${biParams.mainCTAName}`,
        from_url: getCurrentUrl(),
        guid_type: appId ? 'app_id' : 'template_id',
        guid_value: appId || templateId,
        to_url: '',
      }),
    );
  };

  const reportSecondaryCTAClick = ({
    templateId,
    appId,
    biParams,
  }: {
    templateId: string;
    appId?: string;
    biParams: SelfHostedModalBiParams;
  }) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'modal_step_backwards',
        click_name: `${biParams.stepName}: ${biParams.secondaryCTAName}`,
        from_url: getCurrentUrl(),
        guid_type: appId ? 'app_id' : 'template_id',
        guid_value: appId || templateId,
        to_url: '',
      }),
    );
  };

  const reportInputFocused = (templateId: string, inputName: string) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'action',
        click_name: `input clicked: ${inputName}`,
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: '',
      }),
    );
  };

  const reportBreadCrumbsClicked = (templateId: string) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'breadcrumbs_navigation',
        click_name: 'go to main page',
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: '',
      }),
    );
  };

  const reportClickOnDocs = (activeTabId: string, url: string) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'external_url_docs',
        click_name: `go to ${activeTabId} docs`,
        from_url: getCurrentUrl(),
        guid_type: 'irrelevant',
        guid_value: '11111111-1111-1111-1111-111111111111',
        to_url: url,
      }),
    );
  };

  const reportTemplatePageCTAClick = ({
    CtaName,
    destUrl,
    templateId,
  }: {
    CtaName: string;
    destUrl?: string;
    templateId: string;
  }) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'navigation_button',
        click_name: `${CtaName} CTA click`,
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: destUrl || '',
      }),
    );
  };

  const reportExpandCollapseClick = (
    templateId: string,
    isTextExpanded: boolean,
  ) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'action',
        click_name: `instructions read ${
          isTextExpanded ? 'less' : 'more'
        } click`,
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: '',
      }),
    );
  };

  const reportCliPrerequisiteLinkClick = (
    templateId: string,
    name: PrerequisiteStep,
    link: string,
  ) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'external_url_docs',
        click_name: `CLI template link : ${name}`,
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: link,
      }),
    );
  };

  const reportPackageManagerSelected = (
    templateId: string,
    packageManager: PackageManager,
  ) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'action',
        click_name: `terminal example: select ${packageManager} package manager`,
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: '',
      }),
    );
  };

  const reportToggleSecretsClick = (
    templateId: string,
    isSecretShown: boolean,
  ) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'action',
        click_name: `${isSecretShown ? 'hide' : 'show'} secrets clicked`,
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: '',
      }),
    );
  };

  const reportClickOnGithubLink = (templateId: string) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'external_url_open_template',
        click_name: `github repo link click`,
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: '',
      }),
    );
  };

  const reportClickOnTemplate = (templateId: string, externalUrl?: string) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'navigation_button',
        click_name: 'template_grid_item_click',
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: externalUrl,
      }),
    );
  };

  const reportCliOptionsModalLoaded = () => {
    void logger.report(
      buildCliModalLoadedEvent({
        modalName: CLI_APP_OPTIONS_MODAL_NAME,
        route: CLI_APP_OPTIONS_MODAL_ROUTE.TEMPLATES,
      }),
    );
  };

  const reportCliOptionsModalCtaClick = () => {
    void logger.report(
      buildCliModalCtaClickEvent({
        modalName: CLI_APP_OPTIONS_MODAL_NAME,
        route: CLI_APP_OPTIONS_MODAL_ROUTE.TEMPLATES,
        ctaName: 'next',
      }),
    );
  };

  const reportMarkdownLinkClicked = (
    templateId: string,
    url: string,
    name: string,
  ) => {
    void logger.report(
      appTemplatesUserClicked({
        click_type: 'external_url_docs',
        click_name: `template description markdown link ${name}`,
        from_url: getCurrentUrl(),
        guid_type: 'template_id',
        guid_value: templateId,
        to_url: url,
      }),
    );
  };

  return {
    reportCloseButtonClick,
    reportExpandCollapseClick,
    reportToggleSecretsClick,
    reportClickOnDocs,
    reportClickOnTemplate,
    reportMainCTAClick,
    reportSecondaryCTAClick,
    reportCliPrerequisiteLinkClick,
    reportCloseCliModalClick,
    reportInputFocused,
    reportProviderSelected,
    reportAppTestingDocsClicked,
    reportMainPageLoaded,
    reportClickOnTab,
    reportCopyToClipboardClicked,
    reportTemplatePageCTAClick,
    reportTagClicked,
    reportRelatedTemplateClick,
    reportBreadCrumbsClicked,
    reportPackageManagerSelected,
    reportClickOnGithubLink,
    reportImagesCarouselChange,
    reportMarkdownLinkClicked,
    reportCliOptionsModalLoaded,
    reportCliOptionsModalCtaClick,
  };
});
